import { GetWasteShipmentQuery, WasteShipmentStatus } from "generated-graphql/graphql";
import pluralize from "pluralize";

import { formatShipmentDate } from "./shipmentDisplay";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;

/**
 * `terminal` is for a shipment that stopped rather than finished. It reads as
 * halted instead of merely unfinished, which `upcoming` and `inactive` both
 * imply -- those say "not yet", and a canceled shipment's remaining steps are
 * never going to happen.
 */
export type LifecycleState = "done" | "current" | "upcoming" | "inactive" | "terminal";

/**
 * Stable identity for a step, independent of its label.
 *
 * The labels move as the shipment ages -- "Request pickup" becomes "Requested",
 * "Shipped" becomes "Canceled" -- so anything keyed to a step (a React key, the
 * action rendered under it) has to key off this instead.
 */
export type LifecycleStepKey = "packaged" | "request" | "shipped" | "manifest";

export type LifecycleStep = {
  key: LifecycleStepKey;
  label: string;
  state: LifecycleState;
  meta: string;
};

/** The export format a signed request URL was rendered in, for the menu label. */
export function formatFromExportUrl(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }
  const match = /\.(pdf|csv|xlsx)(\?|$)/i.exec(url);
  return match ? match[1].toUpperCase() : null;
}

/**
 * Where the shipment has got to, as ordered steps.
 *
 * Shared by the rail. Kept as data rather than markup so the same reading of
 * "what has happened" can be rendered vertically here and, if it is ever
 * needed, horizontally elsewhere.
 */
export function lifecycleSteps(shipment: Shipment): LifecycleStep[] {
  const isDraft = shipment.status === WasteShipmentStatus.Draft;
  const isRequested = shipment.status === WasteShipmentStatus.Requested;
  const isShipped = shipment.status === WasteShipmentStatus.Shipped;
  const isCanceled = shipment.status === WasteShipmentStatus.Canceled;
  const hasDocument = Boolean(shipment.requestGeneratedAt);
  const containerCount = shipment.lines.length;

  const packaged: LifecycleStep = {
    key: "packaged",
    label: "Packaged",
    state: "done",
    meta: `${formatShipmentDate(shipment.createdAt)} · ${containerCount} ${pluralize(
      "container",
      containerCount
    )}`,
  };

  const requested: LifecycleStep = (() => {
    if (hasDocument) {
      const format = formatFromExportUrl(shipment.requestExportUrl);
      return {
        key: "request",
        label: "Requested",
        state: "done",
        meta: [formatShipmentDate(shipment.requestGeneratedAt), format].filter(Boolean).join(" · "),
      };
    }
    if (isRequested) {
      // The bare transition: someone arranged the pickup outside Encamp, so
      // there is no document and no generated-at stamp to show.
      return { key: "request", label: "Requested", state: "done", meta: "Arranged outside Encamp" };
    }
    if (isDraft) {
      return { key: "request", label: "Request pickup", state: "current", meta: "Optional" };
    }
    return { key: "request", label: "Request pickup", state: "inactive", meta: "No document" };
  })();

  const shipped: LifecycleStep = (() => {
    if (isShipped) {
      return {
        key: "shipped",
        label: "Shipped",
        state: "done",
        meta: formatShipmentDate(shipment.shippedDate) ?? "",
      };
    }
    if (isCanceled) {
      return { key: "shipped", label: "Canceled", state: "terminal", meta: "Nothing shipped" };
    }
    const pickup = shipment.scheduledPickupDate
      ? `Pickup ${formatShipmentDate(shipment.scheduledPickupDate)}`
      : null;
    const planned = shipment.plannedShipDate
      ? `Planned ${formatShipmentDate(shipment.plannedShipDate)}`
      : null;
    return {
      key: "shipped",
      label: "Shipped",
      state: isRequested ? "current" : "upcoming",
      meta: pickup ?? planned ?? "Not shipped yet",
    };
  })();

  // The manifest is a stage of the shipment's life, not a separate feature, so
  // it reads as the step it is. Reconciliation is deliberately NOT a step: it
  // is computed at read and can flip back when a corrected manifest arrives,
  // while every step above it is one-way.
  const manifest: LifecycleStep = (() => {
    if (shipment.manifestReceivedAt) {
      return {
        key: "manifest",
        label: "Manifest received",
        state: "done",
        meta: [
          shipment.reconciliation.linkedManifestTrackingNumber,
          formatShipmentDate(shipment.manifestReceivedAt),
        ]
          .filter(Boolean)
          .join(" · "),
      };
    }
    if (isCanceled) {
      return { key: "manifest", label: "Manifest", state: "inactive", meta: "" };
    }
    return {
      key: "manifest",
      label: "Manifest received",
      state: "upcoming",
      meta: "Not yet attached",
    };
  })();

  return [packaged, requested, shipped, manifest];
}
